import { cardPayOnlineLabel, formatCents, formatCount, gatewayName, parseMoneyToCents, qrPayLabel, tt } from '@agos/shared'

/**
 * `/online-payments` — the card-payment archive, and two checkout rules.
 *
 * **FB-157.** The four stat cards describe fifty rows and are labelled as the business's.
 * `GET /payments` defaults to `limit = 50`:
 *
 * ```java
 * int size = Math.min(limit == null || limit <= 0 ? 50 : limit, MAX_PAGE);
 * ```
 *
 * Legacy passes no limit and no offset, has no pagination control at all, and then computes
 *
 * ```tsx
 * const stats = { total: payments.length, paid: …, volume: payments.filter(paid).reduce(…) };
 * ```
 *
 * On the restored data that is **fifty rows of 1 355**, and the cards read:
 *
 * | Card | Legacy shows | The archive holds |
 * |---|---|---|
 * | transactions | 50 | **1 355** |
 * | paid | 45 | **1 249** |
 * | failed | 5 | **71** |
 * | volume | **810,95 €** | **26 417,25 €** |
 *
 * The volume is out by a factor of thirty-three. And the **status filter changes the totals**: with
 * *paid* selected, `stats.total` becomes the paid count and `stats.failed` becomes zero — so the cards
 * describe the filter rather than the archive, without saying so.
 *
 * **FB-158.** The search box filters the fetched fifty by order number, client-side, while the endpoint
 * accepts no search at all. So typing an order number from last month finds nothing and the page says
 * nothing found — of a payment that is there.
 *
 * **And the money is a float.** `formatCurrency(Number(amount))` and `reduce((s, p) => s + Number(p.amount))`
 * over the paid rows: §14.7 says money is integer cents, and a thousand parsed decimals is exactly the sum
 * that drifts.
 *
 * **The rows carry customer PII the table never shows.** Each payment's `requestData` holds the payer's
 * e-mail, first and last name and the card-holder name; the archive's columns show none of them. Fifty
 * payers' details reach the browser to render a table of amounts and statuses.
 */

export interface PaymentRow {
  id: number
  orderId: number | null
  orderNumber: string | null
  /** Cents, per §14.7 — the API sends a decimal string. */
  amountCents: number
  currency: string
  status: string
  /**
   * Which method the customer actually used - `card_pay`, `qr_pay`, or null when the gateway never
   * named one, which is 103 of the owner's 1 355 rows.
   *
   * `BACKEND.md` §10A.126. Read from the gateway's `selectedPaymentMethod` on settle rather than
   * from what the session asked for, because the bank's page offers the card form and the transfer
   * buttons together and the customer can change their mind.
   */
  method: string | null
  /**
   * What the customer paid with on the bank's page - `card_pay`, `qr_pay`, or null - BACKEND.md §10A.419.
   *
   * `method` above is what the payment was **opened as** since then, so a door QR the customer paid by card reads
   * `qr_pay` there and `card_pay` here; null from a server that predates the field, read as *the same as `method`*.
   */
  rail: string | null
  transactionId: string | null
  paymentRef: string | null
  gateway: string
  paidAt: string | null
  createdAt: string
}

export const PAGE_SIZE = 50

export const STATUSES = ['paid', 'pending', 'failed', 'cancelled', 'refunded'] as const

export type PaymentStatus = (typeof STATUSES)[number]

/**
 * The two methods the archive filters on - the owner's *"add filter for 'CardPay' and filter for
 * 'QR Pay', so we can filter them out"*.
 *
 * **Two, not three.** A row whose method the gateway never named is not a third kind of payment,
 * it is a payment that never reached one - 71 failed, 27 refunded and 5 cancelled of the owner's
 * 1 355. Those are already reachable by their **status**, which is the honest question about them,
 * and a *Neznáme* chip would invite somebody to treat the absence as a category.
 */
export const METHODS = ['card_pay', 'qr_pay'] as const

export type PaymentMethod = (typeof METHODS)[number]

/**
 * The owner's own two words for them.
 *
 * *"one 'CardPay' second one 'QrPay'"* - and `QR Pay` with the space, which is how they wrote it the
 * second time, in the sentence that asked for the filter. **§9.767, owner O129: *CardPay online***, the online card's
 * one name on every screen - and since BACKEND.md §10A.419 the method is what the payment was opened as, so a
 * `card_pay` row is a CardPay online payment and a `qr_pay` row is the owner's QR Pay, whichever rail paid it.
 */
export function methodLabel(method: string | null): string {
  if (method === 'card_pay') return cardPayOnlineLabel()
  if (method === 'qr_pay') return qrPayLabel()
  // A method nobody has labelled is shown as the gateway's own word, which is what the column
  // holds: §10A.126 stores an unrecognised method lowercased rather than dropping it.
  return method === null || method === '' ? '' : method
}

/**
 * What a row with no method should read as in the table.
 *
 * **Not blank, and not a guess.** The gateway never told us, and that is a different fact from
 * *paid by card* - the archive's own promise about `responseData` is that it is the evidence, and
 * for these rows there is none.
 */
export function methodCell(row: { method: string | null; status: string }): string {
  const labelled = methodLabel(row.method)
  if (labelled !== '') return labelled
  return tt('neuvedené')
}

/**
 * What the customer paid with, said under the method only when it differs from it - BACKEND.md §10A.419, owner O129.
 *
 * The owner paid a door QR by card on the bank's page and read *CardPay*; the row says *QR Pay* now, and this line
 * says *zaplatené kartou* under it, so the refund (which a card allows and a transfer does not) still makes sense.
 * Nothing when the rail is the method, unknown, or from a server that predates the field.
 */
export function railNote(row: Pick<PaymentRow, 'method' | 'rail'>): string {
  if (row.rail === null || row.method === null || row.rail === row.method) return ''
  if (row.rail === 'card_pay') return tt('zaplatené kartou')
  if (row.rail === 'qr_pay') return tt('zaplatené prevodom')
  return ''
}

export function statusLabel(status: string): string {
  if (status === 'paid') return tt('Zaplatené')
  if (status === 'pending') return tt('Čaká')
  if (status === 'failed') return tt('Zlyhala')
  if (status === 'cancelled') return tt('Zrušená')
  if (status === 'refunded') return tt('Vrátená')
  // A status nobody has labelled is shown as its key, because the key is what the gateway sent.
  return status
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

/**
 * Read a page of payments.
 *
 * `requestData` is deliberately not carried into the row: it holds the payer's name and e-mail, the table
 * shows neither, and a field in state is a field in every error report.
 */
export function rowsOf(payload: unknown): PaymentRow[] {
  if (!Array.isArray(payload)) return []
  return payload.map((entry) => {
    const row = entry as Record<string, unknown>
    return {
      id: typeof row.id === 'number' ? row.id : 0,
      orderId: typeof row.orderId === 'number' ? row.orderId : null,
      orderNumber: text(row.orderNumber),
      amountCents: parseMoneyToCents(typeof row.amount === 'string' ? row.amount : 0),
      currency: text(row.currency) ?? 'EUR',
      status: text(row.status) ?? '',
      method: text(row.method),
      rail: text(row.rail),
      transactionId: text(row.transactionId),
      paymentRef: text(row.paymentRef),
      gateway: text(row.gateway) ?? '',
      paidAt: text(row.paidAt),
      createdAt: text(row.createdAt) ?? '',
    }
  })
}

// ─── FB-157: what the figures describe ───────────────────────────────────────
//
// `WindowTotals`, `totalsOf`, `isWindowed`, `figureLabel`, `windowNote` and `volumeLabel` were here
// and are deleted — §9.35, 2026-08-06. They were the *first* answer to FB-157: the stat cards were
// computed from whatever page `GET /payments` happened to return, so they read 50 transactions and
// 810,95 € where the archive holds 1 355 and 26 417,25 €, and this cluster's job was to prefix every
// such figure with *najmenej* and explain the window underneath.
//
// The second answer replaced it. `GET /payments/count` reports the real totals, `archiveTotalsOf`
// reads them and the cards state them outright — so there is no window left to caveat, and the
// entry points were never called by the screen that shipped. Deleted rather than kept as a second
// way to describe the same cards: a helper that says "at least 1 355" beside a card that knows it is
// exactly 1 355 is worse than nothing.

// ─── FB-158: the search ──────────────────────────────────────────────────────

/**
 * Filter the fetched rows by order number.
 *
 * **The server searches now** (FB-158), so this is no longer the whole of it — `GET /payments?search=`
 * matches the order number or the payment reference across the archive rather than across the fifty rows
 * that happened to come back. This stays as the local narrowing while the request is in flight, so typing
 * still feels immediate; the authoritative answer replaces it when it lands.
 */
export function searchRows(rows: readonly PaymentRow[], query: string): PaymentRow[] {
  const needle = query.trim().toLowerCase()
  if (needle === '') return [...rows]
  return rows.filter((row) => (row.orderNumber ?? '').toLowerCase().includes(needle))
}

// `searchNote` was here and is deleted with them. It returned the empty string unconditionally —
// FB-158 made the server search the whole archive, so the warning it used to carry had nothing left
// to say. A function whose only behaviour is to return '' is a fossil, and its test asserted exactly
// that.

// ─── FB-157: the totals, from the server ─────────────────────────────────────

/**
 * What the archive actually holds, as `GET /payments/count` reports it.
 *
 * The four stat cards were computed from whatever the last request returned — on the restored data that
 * meant *50 transactions, 810,95 €* where the archive holds **1 355** and **26 417,25 €**, a factor of
 * thirty-three. `null` while the request is in flight, so a screen says nothing rather than a page total.
 */
export interface ArchiveTotals {
  total: number
  paidCount: number
  pendingCount: number
  failedCount: number
  refundedCount: number
  /** §10A.126 - off the same scan as the statuses, so a chip costs no extra request. */
  cardPayCount: number
  qrPayCount: number
  paidCents: number
  /**
   * §10A.419, owner O129 - the paid volume of the payments opened as CardPay online, and of those opened as QR Pay;
   * `null` from a server that does not send them, which a card draws as `-` rather than as a nought.
   */
  paidCardPayCents: number | null
  paidQrPayCents: number | null
}

export function archiveTotalsOf(payload: unknown): ArchiveTotals | null {
  if (typeof payload !== 'object' || payload === null) return null
  const row = payload as Record<string, unknown>
  if (typeof row.total !== 'number') return null
  return {
    total: row.total,
    paidCount: typeof row.paidCount === 'number' ? row.paidCount : 0,
    pendingCount: typeof row.pendingCount === 'number' ? row.pendingCount : 0,
    failedCount: typeof row.failedCount === 'number' ? row.failedCount : 0,
    refundedCount: typeof row.refundedCount === 'number' ? row.refundedCount : 0,
    cardPayCount: typeof row.cardPayCount === 'number' ? row.cardPayCount : 0,
    qrPayCount: typeof row.qrPayCount === 'number' ? row.qrPayCount : 0,
    // Cents, per §14.7 — the money was a parsed float summed across a thousand rows.
    paidCents: parseMoneyToCents(typeof row.paidTotal === 'number' || typeof row.paidTotal === 'string' ? row.paidTotal : 0),
    paidCardPayCents: moneyOrNull(row.paidCardPayTotal),
    paidQrPayCents: moneyOrNull(row.paidQrPayTotal),
  }
}

/** Cents of a figure the server sent, or `null` for one it did not - never a fabricated nought. */
function moneyOrNull(value: unknown): number | null {
  return typeof value === 'number' || (typeof value === 'string' && value !== '') ? parseMoneyToCents(value) : null
}

/**
 * Whether the rows on screen are the whole of what matched.
 *
 * Answered against the server's count rather than against the page size, so a filter that genuinely
 * returns fewer than fifty is not described as a truncated window.
 */
export function isTruncated(rows: readonly PaymentRow[], totals: ArchiveTotals | null): boolean {
  return totals !== null && rows.length < totals.total
}

/** A keystroke is not a query. Long enough to stop typing, short enough not to feel stuck. */
export const SEARCH_DEBOUNCE_MS = 300

/**
 * What the figures describe, said before the figures.
 *
 * Three states, because two would hide the one that matters. The count in flight is not the same as
 * *these are all of them* — a screen that assumed the good case while waiting would state a page as a
 * total, which is the defect this whole item is about.
 */
export function archiveNote(shown: number, totals: ArchiveTotals | null): string {
  if (totals === null) {
    return tt('Načítavam, koľko transakcií archív obsahuje…')
  }
  if (shown >= totals.total) {
    return `${tt('Zobrazené sú všetky')} ${formatCount(totals.total)} ${tt('transakcie tohto výberu.')}`
  }
  return `${tt('Zobrazených je')} ${formatCount(shown)} ${tt('z')} ${formatCount(totals.total)} - ${tt('čísla vyššie sú z celého archívu, nie z tejto strany.')}`
}

// ─── The refund ──────────────────────────────────────────────────────────────

export type RefundBlock = 'not-paid' | 'no-transaction' | null

/**
 * Whether a refund can even be attempted.
 *
 * `OnlinePaymentService.refund` refuses a payment that is not `paid`, one with no transaction id, an
 * unconfigured gateway, and a **bank transfer** — *"a transfer is refunded by making another transfer; the
 * gateway has nothing to reverse."* The first two are visible in the row; the last two are not, so the button
 * is offered and the reason arrives from the server.
 */
export function refundBlock(row: PaymentRow): RefundBlock {
  if (row.status !== 'paid') return 'not-paid'
  if (row.transactionId === null) return 'no-transaction'
  return null
}

export function refundBlockMessage(block: RefundBlock): string {
  if (block === 'not-paid') return tt('Vrátiť sa dá len zaplatená transakcia.')
  if (block === 'no-transaction') return tt('Bez Transaction ID sa vrátenie nedá vykonať.')
  return ''
}

export function refundConfirmation(row: PaymentRow): string {
  const reference = row.orderNumber ?? String(row.id)
  return `${tt('Objednávka')} ${reference}: ${formatCents(row.amountCents)}. ${tt('Peniaze idú zákazníkovi späť cez')} ${gatewayName(row.gateway)} ${tt('a vrátenie sa nedá zrušiť. Prevod (nie kartu) banka vrátiť nevie - to server odmietne.')}`
}

/** A card gateway's name for a person - moved to `@agos/shared` by §9.795, re-exported for this module's callers. */
export { gatewayName }

// ─── The two checkout rules ──────────────────────────────────────────────────
//
// §9.766, owner O127 - *Pravidlá pokladne* left this page for *Nastavenia rozvozu → Platby zákazníkov*, with the two
// switches that decide which ways a customer may pay, and their reading, payload and note went with them
// (`manager-delivery`'s `customer-payments.ts`). Removed rather than left: a helper nothing calls reads as a supported
// way to save the rules from here.

/**
 * FB-817, §9.538 - **`unknown` is a state, not a suppressed message.**
 *
 * A failed `GET /settings/payments` used to reach `unconfigured`, so the screen said *the gateway is not
 * configured* on an installation running live CardPay with 2 252 payment rows - and the owner's open complaint is
 * customers unable to pay by card, so that is the screen they open to investigate it. Folding a failure into
 * `unconfigured` produced the defect and folding it into `live` would be worse; it needs its own answer.
 *
 * Exhaustive in `gatewayMessage`, so a sixth state is a compile error rather than a silent fall-through - §9.507's
 * guarantee, which caught a real defect when `mapy` was added to `MAP_APPS`.
 */
export type GatewayState = 'live' | 'test' | 'inactive' | 'unconfigured' | 'unknown'

export function gatewayState(
  settings: Record<string, unknown> | undefined | null,
): GatewayState {
  /* **`null` is *the read failed* and `undefined` stays *nothing is stored*.** Two absences with different
     meanings, which is this session's *absent is not false* rule again: a franchise that has configured nothing
     is a real state an operator can act on, and a read that did not answer is not. */
  if (settings === null) return 'unknown'
  if (settings === undefined) return 'unconfigured'
  /* §9.793 - **the chosen gateway's own readiness**, which is the server's `Configuration.usable()`: TatraPay's
     client secret, or GP webpay's merchant number and both keys. Reading TatraPay's secret on a GP webpay franchise
     would call a working gateway unconfigured, or a broken one live. */
  const configured =
    settings.gateway === 'gpwebpay'
      ? settings._hasGpwebpayKey === true &&
        settings._hasGpwebpayPublicKey === true &&
        typeof settings.gpwebpayMerchantNumber === 'string' &&
        settings.gpwebpayMerchantNumber.trim() !== ''
      : settings._hasClientSecret === true
  if (!configured) return 'unconfigured'
  if (settings.isActive !== true) return 'inactive'
  return settings.testMode === true ? 'test' : 'live'
}

export function gatewayMessage(state: GatewayState, gateway: string): string {
  /* **Says what is unknown AND what is not affected**, because an operator must not read *we cannot tell* as
     *cards are off*: whether the till offers the card button is decided by the server from these same settings,
     not by this screen, so a failed read here changes nothing a customer sees. */
  if (state === 'unknown') {
    return tt('Stav platobnej brány sa nepodarilo načítať - na platby to nemá vplyv, rozhoduje o nich server.')
  }
  if (state === 'unconfigured') {
    // The sentence §9.538 pins is kept whole - and so is its translation - with the gateway named before it.
    return `${gatewayName(gateway)}: ${tt('Platobná brána nie je nakonfigurovaná - karta sa v pokladni neponúkne.')} ${tt('Kľúče brány zadáva správca platformy (Tower - Integrácie).')}`
  }
  if (state === 'inactive') return `${gatewayName(gateway)}: ${tt('Brána je nakonfigurovaná, ale vypnutá.')}`
  if (state === 'test') {
    return `${gatewayName(gateway)}: ${tt('testovací režim - platby nie sú skutočné.')}`
  }
  return `${gatewayName(gateway)}: ${tt('ostrý režim.')}`
}

/**
 * What *Otestovať spojenie* found - FRONTEND.md §9.793.
 *
 * `GET /payments/test-connection` existed with no caller. For TatraPay+ it asks for a token; for GP webpay it reads
 * the keys, asks the WS for `echo`, and - §9.794 - asks about a payment that cannot exist, which only a merchant GPE
 * knows, signing with a key GPE has, can ask. The step that failed is said, because the next action differs: `keys`
 * and `merchant` are the Tower's (retype the password, upload the key in GPE's portal, check the merchant number and
 * the environment), the rest is the gateway answering or not.
 */
export function connectionTestNote(
  result: unknown,
  gateway: string,
): { ok: boolean; text: string } {
  const row = (typeof result === 'object' && result !== null ? result : {}) as Record<string, unknown>
  const name = gatewayName(gateway)
  const error = typeof row.error === 'string' && row.error.trim() !== '' ? row.error.trim() : null
  if (row.ok === true) {
    const mode = row.testMode === true ? tt('testovací režim') : tt('ostrý režim')
    // §9.794 - `merchant` is GPE answering a signed question, which proves the merchant number and the key too.
    const answers = row.step === 'merchant' ? tt('brána odpovedá a pozná obchodníka aj jeho kľúč') : tt('brána odpovedá')
    return { ok: true, text: `${name}: ${answers} (${mode}).` }
  }
  if (row.step === 'merchant') {
    return {
      ok: false,
      text: `${name}: ${tt('brána odpovedá, ale číslo obchodníka alebo kľúč neprijala')}${error === null ? '.' : ` - ${error}`} ${tt('Opravuje sa v Tower - Integrácie.')}`,
    }
  }
  if (row.step === 'keys' || row.step === 'config') {
    return {
      ok: false,
      text: `${name}: ${tt('nastavenie sa nedá použiť')}${error === null ? '.' : ` - ${error}`} ${tt('Opravuje sa v Tower - Integrácie.')}`,
    }
  }
  return {
    ok: false,
    text: `${name}: ${tt('brána neodpovedá')}${error === null ? '.' : ` - ${error}`}`,
  }
}

/**
 * What one manual sweep did, as a sentence - §10A.194.
 *
 * **Why the figures and not a bare success toast.** The sweep asks the bank about every card payment
 * nobody came back from and then *changes orders*: one becomes paid, another refunded, another
 * failed. *Hotovo* would tell somebody who just pressed a button that touches money nothing about
 * what it touched. The five counts are the job's own log line, returned rather than written to a
 * file on the server.
 *
 * **`looked === 0` is the common answer and it is not a failure.** Nothing is stale most of the
 * time, and a red toast for that would teach a manager to stop pressing it.
 *
 * **Not the generated `Swept`, deliberately.** The client's model has all five fields optional -
 * springdoc marks nothing required off a bare Java record - so it types every count as
 * `number | undefined` and a screen reading it has to decide what an absent one means anyway.
 * This is that decision, made once: `sweptOf` narrows the wire shape into the strict one below, and
 * refuses rather than guessing when the answer is not the shape at all.
 */
export interface SweptCounts {
  looked: number
  paid: number
  failed: number
  refunded: number
  waiting: number
}

export function sweptOf(result: unknown): SweptCounts | null {
  if (typeof result !== 'object' || result === null) return null
  const row = result as Record<string, unknown>
  const count = (key: string): number => {
    const value = row[key]
    return typeof value === 'number' ? value : 0
  }
  // `looked` is the one that must really be there: the other four are 0 on a quiet sweep, so
  // defaulting them is right, while defaulting `looked` would turn an unparseable answer into a
  // confident "nothing was pending".
  if (typeof row.looked !== 'number') return null
  return {
    looked: row.looked,
    paid: count('paid'),
    failed: count('failed'),
    refunded: count('refunded'),
    waiting: count('waiting'),
  }
}

/** The bound the server applies to one press - `OnlinePaymentService.MANUAL_SWEEP_LIMIT`. */
export const SWEEP_LIMIT = 100

export function sweepNote(swept: SweptCounts): string {
  if (swept.looked === 0) return tt('Žiadna platba nečaká na overenie.')

  const parts: string[] = []
  if (swept.paid > 0) parts.push(`${swept.paid}× ${tt('zaplatené')}`)
  if (swept.refunded > 0) parts.push(`${swept.refunded}× ${tt('vrátené')}`)
  if (swept.failed > 0) parts.push(`${swept.failed}× ${tt('neúspešné')}`)
  if (swept.waiting > 0) parts.push(`${swept.waiting}× ${tt('stále čaká')}`)

  const looked = `${tt('Overených platieb')}: ${swept.looked}`
  // Every outcome bucket empty with rows looked at means the gateway answered nothing useful for any
  // of them - worth saying, rather than printing a bare count that reads as success.
  const outcome = parts.length === 0 ? tt('bez zmeny') : parts.join(', ')
  const more =
    swept.looked >= SWEEP_LIMIT ? ` - ${tt('môže ich byť viac, spustite znova')}` : ''
  return `${looked} - ${outcome}${more}`
}

/**
 * One card order that never reached the bank - `BACKEND.md` §10A.207, FB-764.
 *
 * Only the fields the panel draws. The wire shape is `UnreconciledCardOrder`; this narrows it so a
 * test can build one without twelve properties it does not assert.
 */
export interface UnreconciledOrder {
  id?: number
  orderNumber?: string
  total?: string
  customerName?: string
  customerPhone?: string
  createdAt?: string
}

/**
 * What the panel says above the list, and it has to say what the state *is* - FB-764.
 *
 * **The owner found this from the order side** because it is the only side that shows it: *"its
 * cardapy and we see «Čaká na overenie» but in Platba Online - archív there its not"*. So the
 * sentence names the cause rather than the count alone - an operator who reads *3 objednávky* and
 * nothing else learns that something is wrong and not what.
 *
 * **Empty is the good state and says so plainly**, rather than drawing a panel of nothing: §9.410's
 * distinction, and the same reason the sweep note above answers *Žiadna platba nečaká na overenie*.
 */
export function unreconciledNote(count: number): string {
  if (count === 0) return tt('Každá kartová platba má svoju reláciu.')
  return `${formatCount(count)} ${tt('objednávok začalo kartovú platbu a nemá reláciu - zákazník sa pravdepodobne nedostal k banke. Nie sú v archíve ani ich nevie dokončiť automatické overenie.')}`
}

/**
 * The money in question, in cents - FB-764.
 *
 * Said because *three orders* and *three orders worth 94,20 EUR* are different questions, and the
 * second is the one that decides whether somebody rings them today.
 *
 * **Kept as the fallback rather than the figure** - §9.638. A backend older than §10A.322 answers no
 * totals, and this is what the panel draws then; what it must not be is the figure of record, because
 * past the server's cap it is short by every row beyond it.
 */
export function unreconciledTotalCents(orders: readonly UnreconciledOrder[]): number {
  return orders.reduce((sum, order) => sum + parseMoneyToCents(order.total ?? 0), 0)
}

/**
 * The rows, whichever shape the server answers - §9.638, §10A.322.
 *
 * **Both shapes, because the response changed.** §10A.322 wraps the list as `{orders, total,
 * totalCents, limit}`; a reader that understood only the new one would draw an **empty panel** against
 * a server mid-deploy, which on this screen reads as *every card payment has its session* - the one
 * answer FB-817 established it must never give.
 */
export function unreconciledOf(payload: unknown): UnreconciledOrder[] {
  if (Array.isArray(payload)) return payload as UnreconciledOrder[]
  const wrapped = (payload as { orders?: unknown } | undefined)?.orders
  return Array.isArray(wrapped) ? (wrapped as UnreconciledOrder[]) : []
}

/**
 * How many such orders there are, what they are worth, and what the page held - §9.638.
 *
 * **FB-963.** The panel drew its count from `orders.length` and its money from the reduction above,
 * over a page the server caps at fifty - so past fifty the header, the total and the sentence were all
 * short about money a customer may never have paid. Measured on the owner's rows: 38 of 50 at Šurany,
 * worth 456,10 EUR, on a set that never shrinks.
 *
 * **Read first, reduce second**, and the order is the behaviour: a fallback evaluated first is a
 * fallback that always runs, so the figure would stay short with the fix in place.
 *
 * **Both published figures or neither.** A half-read object would put a server count beside a reduced
 * total, which is two answers to one question - so the pair is taken together and the fallback covers
 * them together.
 */
export interface UnreconciledFigures {
  count: number
  cents: number
  /** What the server capped the page at, or null when it did not say. */
  limit: number | null
}

export function unreconciledFigures(
  payload: unknown,
  orders: readonly UnreconciledOrder[],
): UnreconciledFigures {
  const row = (payload ?? {}) as Record<string, unknown>
  const total = figure(row.total)
  const cents = figure(row.totalCents)
  if (total !== null && cents !== null) {
    return { count: total, cents, limit: figure(row.limit) }
  }
  return { count: orders.length, cents: unreconciledTotalCents(orders), limit: figure(row.limit) }
}

function figure(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value)
  }
  return null
}

/**
 * *50 z 213 objednávok*, or empty when the panel is showing all of them - §9.638.
 *
 * **Its own sentence rather than `truncationNote`'s**, and that is a decision §3.172 recorded: that
 * helper renders *záznamov* and its javadoc scopes it to the audit trail, so printing *records* over a
 * list of orders would be wrong, and a noun parameter would edit that page, its pinned cases and its
 * marker for no gain. The shared thing is the rule; the two screens count different objects.
 *
 * **Empty when nothing is hidden**, which is `truncationNote`'s own reasoning - a note on every page
 * is a note nobody reads.
 */
export function unreconciledTruncation(shown: number, figures: UnreconciledFigures): string {
  if (figures.count <= shown) return ''
  return `${formatCount(shown)} ${tt('z')} ${formatCount(figures.count)} ${tt('objednávok')}`
}

